import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';
import Uppy from '@uppy/core';
import Dashboard from '@uppy/dashboard';
import ImageEditor from '@uppy/image-editor';
import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/image-editor/css/style.min.css';
import QRCodeCard from '../components/QRCodeCard';
import QRCodeImageModal from '../components/QRCodeImageModal';
import UtilityServiceTemplateSettings from '../components/UtilityServiceTemplateSettings';
import { fetchBusinessAccounts } from '../store/slices/businessAccountsSlice';
import './Settings.css';

const DEFAULT_BUSINESS_HOURS = {
  monday:    { enabled: true,  start: '09:00', end: '18:00' },
  tuesday:   { enabled: true,  start: '09:00', end: '18:00' },
  wednesday: { enabled: true,  start: '09:00', end: '18:00' },
  thursday:  { enabled: true,  start: '09:00', end: '18:00' },
  friday:    { enabled: true,  start: '09:00', end: '18:00' },
  saturday:  { enabled: true,  start: '10:00', end: '18:00' },
  sunday:    { enabled: false, start: '10:00', end: '14:00' },
};

const Settings = () => {
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile state
  const [profile, setProfile] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    timezone: 'UTC',
    language: 'en',
    businessDescription: '',
  });

  // Business Hours state
  const [businessHours, setBusinessHours] = useState(DEFAULT_BUSINESS_HOURS);
  const [autoReply, setAutoReply] = useState(false);
  const [autoReplyMessage, setAutoReplyMessage] = useState('');
  const [timezone, setTimezone] = useState('UTC');

  // Notifications state
  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    smsNotifications: false,
    pushNotifications: true,
    newMessageAlerts: true,
    broadcastAlerts: true,
    templateAlerts: true,
  });

  // Security state
  const [security, setSecurity] = useState({
    twoFactorAuthEnabled: false,
    sessionTimeout: 30,
    ipWhitelist: [],
  });
  const [newIp, setNewIp] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // WhatsApp API state (read-only, from WABA status)
  const [wabaStatus, setWabaStatus] = useState(null);
  const [loadingWaba, setLoadingWaba] = useState(false);

  // QR Codes state
  const [qrCodeFormat, setQrCodeFormat] = useState('SVG');
  const [qrCodes, setQrCodes] = useState([]);
  const [loadingQR, setLoadingQR] = useState(false);
  const [showQrCreateForm, setShowQrCreateForm] = useState(false);
  const [qrCreateForm, setQrCreateForm] = useState({ prefilledMessage: '', generateQrImage: 'SVG' });
  const [qrSaving, setQrSaving] = useState(false);
  const [editingQRCode, setEditingQRCode] = useState(null); // { code, prefilledMessage }
  const [viewingQRCode, setViewingQRCode] = useState(null);

  // Business Data state
  const [businessData, setBusinessData] = useState({
    storeName: '',
    storeAddress: '',
    storeCity: '',
    storeState: '',
    storeZip: '',
    storeCountry: '',
    storePhone: '',
    storeEmail: '',
    storeWebsite: '',
    gstNumber: '',
    panNumber: '',
    bankAccountNumber: '',
    ifscCode: '',
    logoUrl: '',
  });
  const [loadingBusinessData, setLoadingBusinessData] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [hasLogoFile, setHasLogoFile] = useState(false);
  const logoDashboardRef = useRef(null);

  const authUser = useSelector(state => state.auth?.user || {});
  const { businessAccountId, phoneNumberId, wabaId: authWabaId } = authUser;
  const { currentAccount, accounts } = useSelector(state => state.businessAccounts || {});
  const resolvedWabaId = currentAccount?.wabaId || accounts?.[0]?.wabaId || authWabaId;

  const tabs = [
    { id: 'profile',        label: 'Profile'        },
    { id: 'whatsapp',       label: 'WhatsApp API'   },
    { id: 'utility-service', label: 'Utility Service' },
    { id: 'notifications',  label: 'Notifications'  },
    { id: 'business-hours', label: 'Business Hours' },
    { id: 'security',       label: 'Security'       },
    { id: 'qr-code',        label: 'QR Code'        },
    { id: 'business-data',  label: 'Invoice Business Details'  },
    { id: 'team',           label: 'Team'           },
  ];

  const daysOfWeek = [
    { key: 'monday',    label: 'Monday'    },
    { key: 'tuesday',   label: 'Tuesday'   },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday',  label: 'Thursday'  },
    { key: 'friday',    label: 'Friday'    },
    { key: 'saturday',  label: 'Saturday'  },
    { key: 'sunday',    label: 'Sunday'    },
  ];

  
  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const res = await apiService.auth.getProfile();
        const u = res.data?.data || res.data || {};
        setProfile({
          firstName:           u.firstName           || '',
          lastName:            u.lastName            || '',
          email:               u.email               || '',
          phone:               u.phone               || '',
          timezone:            u.timezone            || 'UTC',
          language:            u.language            || 'en',
          businessDescription: u.businessDescription || '',
        });
        setTimezone(u.timezone || 'UTC');
        setNotifications({
          emailNotifications: u.notifications?.emailNotifications ?? true,
          smsNotifications:   u.notifications?.smsNotifications   ?? false,
          pushNotifications:  u.notifications?.pushNotifications  ?? true,
          newMessageAlerts:   u.notifications?.newMessageAlerts   ?? true,
          broadcastAlerts:    u.notifications?.broadcastAlerts    ?? true,
          templateAlerts:     u.notifications?.templateAlerts     ?? true,
        });
        setSecurity({
          twoFactorAuthEnabled: u.twoFactorAuthEnabled ?? false,
          sessionTimeout:       u.sessionTimeout       ?? 30,
          ipWhitelist:          u.ipWhitelist          || [],
        });
        if (u.businessHours) {
          setBusinessHours({ ...DEFAULT_BUSINESS_HOURS, ...u.businessHours });
          setTimezone(u.timezone || 'UTC');
        }
        if (u.autoReply !== undefined) setAutoReply(u.autoReply);
        if (u.autoReplyMessage)       setAutoReplyMessage(u.autoReplyMessage);
      } catch (err) {
        console.error('Failed to load profile:', err);
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    dispatch(fetchBusinessAccounts());
  }, [dispatch]);

  useEffect(() => {
    if (activeTab === 'whatsapp' && !wabaStatus) {
      const fetchWaba = async () => {
        setLoadingWaba(true);
        try {
          const res = await apiService.waba.getStatus();
          setWabaStatus(res.data?.data || res.data || null);
        } catch {
          setWabaStatus(null);
        } finally {
          setLoadingWaba(false);
        }
      };
      fetchWaba();
    }
  }, [activeTab, wabaStatus]);

  const fetchCachedQRCodes = useCallback(async () => {
    setLoadingQR(true);
    try {
      const res = await apiService.qrCodes.getCached();
      setQrCodes(res.data?.data || []);
    } catch {
      toast.error('Failed to load QR codes');
    } finally {
      setLoadingQR(false);
    }
  }, [businessAccountId, phoneNumberId]);

  useEffect(() => {
    if (activeTab === 'qr-code') {
      fetchCachedQRCodes();
    }
  }, [activeTab, businessAccountId, phoneNumberId, fetchCachedQRCodes]);

  // ─── Load Business Data when tab is active ────────────────────────────────
  useEffect(() => {
    if (activeTab === 'business-data' && !businessData.storeName && !loadingBusinessData) {
      const fetchBusinessData = async () => {
        setLoadingBusinessData(true);
        try {
          const res = await apiService.auth.getBusinessData();
          const d = res.data?.data || res.data || {};
          setBusinessData({
            storeName:         d.storeName         || '',
            storeAddress:      d.storeAddress      || '',
            storeCity:         d.storeCity         || '',
            storeState:        d.storeState        || '',
            storeZip:          d.storeZip          || '',
            storeCountry:      d.storeCountry      || '',
            storePhone:        d.storePhone        || '',
            storeEmail:        d.storeEmail        || '',
            storeWebsite:      d.storeWebsite      || '',
            gstNumber:         d.gstNumber         || '',
            panNumber:         d.panNumber         || '',
            bankAccountNumber: d.bankAccountNumber || '',
            ifscCode:          d.ifscCode          || '',
            logoUrl:           d.logoUrl           || '',
          });
        } catch {
          toast.error('Failed to load business data');
        } finally {
          setLoadingBusinessData(false);
        }
      };
      fetchBusinessData();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

 const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await apiService.auth.updateProfile({
        firstName:           profile.firstName,
        lastName:            profile.lastName,
        timezone:            profile.timezone,
        language:            profile.language,
        businessDescription: profile.businessDescription,
      });
      toast.success('Profile saved successfully');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBusinessHours = async () => {
    setSaving(true);
    try {
      await apiService.auth.updateBusinessHours({
        businessHours,
        autoReply,
        autoReplyMessage,
        timezone,
      });
      toast.success('Business hours saved successfully');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save business hours');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveNotifications = async () => {
    setSaving(true);
    try {
      await apiService.auth.updateNotifications(notifications);
      toast.success('Notification preferences saved');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save notifications');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecurity = async () => {
    setSaving(true);
    try {
      await apiService.auth.updateSecurity({
        twoFactorAuthEnabled: security.twoFactorAuthEnabled,
        sessionTimeout:       security.sessionTimeout,
        ipWhitelist:          security.ipWhitelist,
      });
      toast.success('Security settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save security settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }
    if (newPassword.length < 8 || !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      toast.error('New password must be at least 8 characters and include uppercase, lowercase, and a number');
      return;
    }
    setChangingPassword(true);
    try {
      await apiService.auth.changePassword({ currentPassword, newPassword, confirmPassword });
      toast.success('Password changed successfully');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAddIp = () => {
    const ip = newIp.trim();
    if (!ip) return;
    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}(\/\d+)?$/;
    const ipv6 = /^[0-9a-fA-F:]+$/;
    if (!ipv4.test(ip) && !ipv6.test(ip)) {
      toast.error('Enter a valid IP address');
      return;
    }
    if (security.ipWhitelist.includes(ip)) {
      toast.error('IP already in whitelist');
      return;
    }
    setSecurity(prev => ({ ...prev, ipWhitelist: [...prev.ipWhitelist, ip] }));
    setNewIp('');
  };

  const handleRemoveIp = (ip) => {
    setSecurity(prev => ({ ...prev, ipWhitelist: prev.ipWhitelist.filter(i => i !== ip) }));
  };

  const handleGenerateQRCode = async () => {
  
    setLoadingQR(true);
    try {
      const res = await apiService.qrCodes.sync({ format: qrCodeFormat });
      setQrCodes(res.data?.data || []);
      toast.success('QR codes synced successfully');
    } catch (err) {
      toast.error(err?.message || 'Failed to sync QR codes');
    } finally {
      setLoadingQR(false);
    }
  };

  const handleDownloadQRCode = (qrCode) => {
    if (!qrCode.qrImageUrl) { toast.error('QR code image not available'); return; }
    const link = document.createElement('a');
    link.href = qrCode.qrImageUrl;
    link.download = `qr-code-${qrCode.code}.${qrCode.imageFormat === 'PNG' ? 'png' : 'svg'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('QR code downloaded');
  };

  const handleCopyQRCode = (qrCode) => {
    if (!qrCode.deepLinkUrl) { toast.error('Deep link not available'); return; }
    navigator.clipboard.writeText(qrCode.deepLinkUrl);
    toast.success('Deep link copied to clipboard');
  };

  const handleDeleteQRCode = async (code) => {
    if (!window.confirm('Delete this QR code?')) return;
    setLoadingQR(true);
    try {
      await apiService.qrCodes.delete(code);
      setQrCodes(prev => prev.filter(q => q.code !== code));
      toast.success('QR code deleted');
    } catch {
      toast.error('Failed to delete QR code');
    } finally {
      setLoadingQR(false);
    }
  };

  const handleCreateQRCode = async (e) => {
    e.preventDefault();
    if (!qrCreateForm.prefilledMessage.trim()) {
      toast.error('Prefilled message is required');
      return;
    }
    setQrSaving(true);
    try {
      const res = await apiService.qrCodes.create({
        prefilled_message: qrCreateForm.prefilledMessage.trim(),
        generate_qr_image: qrCreateForm.generateQrImage,
      });
      const newCode = res.data?.data || res.data;
      setQrCodes(prev => [newCode, ...prev]);
      setQrCreateForm({ prefilledMessage: '', generateQrImage: 'SVG' });
      setShowQrCreateForm(false);
      toast.success('QR code created successfully');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to create QR code');
    } finally {
      setQrSaving(false);
    }
  };

  const handleUpdateQRCode = async (e) => {
    e.preventDefault();
    if (!editingQRCode?.prefilledMessage?.trim()) {
      toast.error('Prefilled message is required');
      return;
    }
    setQrSaving(true);
    try {
      const res = await apiService.qrCodes.update(editingQRCode.code, {
        prefilled_message: editingQRCode.prefilledMessage.trim(),
      });
      const updated = res.data?.data || res.data;
      setQrCodes(prev => prev.map(q => q.code === updated.code ? updated : q));
      setEditingQRCode(null);
      toast.success('QR code updated');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to update QR code');
    } finally {
      setQrSaving(false);
    }
  };

  // Business Data handlers
  const handleSaveBusinessData = async () => {
    setSaving(true);
    try {
      await apiService.auth.updateBusinessData({
        storeName:         businessData.storeName,
        storeAddress:      businessData.storeAddress,
        storeCity:         businessData.storeCity,
        storeState:        businessData.storeState,
        storeZip:          businessData.storeZip,
        storeCountry:      businessData.storeCountry,
        storePhone:        businessData.storePhone,
        storeEmail:        businessData.storeEmail,
        storeWebsite:      businessData.storeWebsite,
        gstNumber:         businessData.gstNumber,
        panNumber:         businessData.panNumber,
        bankAccountNumber: businessData.bankAccountNumber,
        ifscCode:          businessData.ifscCode,
      });
      toast.success('Business data saved successfully');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save business data');
    } finally {
      setSaving(false);
    }
  };

  // ─── Uppy instance for logo upload with ImageEditor ──────────────────────
  const logoUppy = useMemo(() => {
    const instance = new Uppy({
      id: 'logoUppy',
      restrictions: {
        maxNumberOfFiles: 1,
        allowedFileTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
        maxFileSize: 5 * 1024 * 1024,
      },
      autoProceed: false,
    });

    instance.on('file-editor:complete', () => {
      setHasLogoFile(true);
    });

    instance.on('file-added', () => {
      setHasLogoFile(true);
    });

    instance.on('file-removed', () => {
      setHasLogoFile(instance.getFiles().length > 0);
    });

    instance.on('cancel-all', () => {
      setHasLogoFile(false);
    });

    return instance;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Mount/unmount Dashboard + ImageEditor when business-data tab is active
  useEffect(() => {
    if (activeTab !== 'business-data' || !logoDashboardRef.current) return undefined;

    if (!logoUppy.getPlugin('ImageEditor')) {
      logoUppy.use(ImageEditor, {
        id: 'ImageEditor',
        quality: 0.9,
        cropperOptions: {
          viewMode: 1,
          background: false,
          aspectRatio: NaN,
          autoCropArea: 1,
          responsive: true,
          restore: false,
          guides: true,
          center: true,
          highlight: true,
          cropBoxMovable: true,
          cropBoxResizable: true,
        },
        actions: {
          revert: true, rotate: true, granularRotate: true,
          flip: true, zoomIn: true, zoomOut: true,
          cropSquare: true, cropWidescreen: true,
        },
      });
    }

    if (!logoUppy.getPlugin('Dashboard')) {
      logoUppy.use(Dashboard, {
        target: logoDashboardRef.current,
        inline: true,
        width: '100%',
        height: 320,
        showProgressDetails: true,
        proudlyDisplayPoweredByUppy: true,
        note: 'JPG, PNG, WebP — max 5 MB',
        autoOpenImageEditor: true,
      });
    }

    return () => {
      const dashboard = logoUppy.getPlugin('Dashboard');
      if (dashboard) logoUppy.removePlugin(dashboard);

      const imageEditor = logoUppy.getPlugin('ImageEditor');
      if (imageEditor) logoUppy.removePlugin(imageEditor);
    };
  }, [activeTab, loadingBusinessData, logoUppy]);

  const handleLogoUpload = async () => {
    const files = logoUppy.getFiles();
    if (!files.length) {
      toast.error('Please select an image first');
      return;
    }
    const file = files[0];
    const blob = file.data instanceof Blob ? file.data : null;
    if (!blob) {
      toast.error('The selected image is not ready yet');
      return;
    }
    const formPayload = new FormData();
    formPayload.append('logo', blob, file.name || 'business-logo');
    setUploadingLogo(true);
    try {
      const res = await apiService.auth.uploadBusinessLogo(formPayload);
      const responseData = res.data?.data || res.data || {};
      const url = responseData.logoUrl || responseData.logo || responseData.url || '';
      setBusinessData(d => ({ ...d, logoUrl: url || d.logoUrl }));
      setHasLogoFile(false);
      logoUppy.cancelAll();
      toast.success('Logo uploaded successfully');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleGlobalSave = () => {
    switch (activeTab) {
      case 'profile':        return handleSaveProfile();
      case 'notifications':  return handleSaveNotifications();
      case 'business-hours': return handleSaveBusinessHours();
      case 'security':       return handleSaveSecurity();
      case 'business-data':  return handleSaveBusinessData();
      default:               return;
    }
  };

  const showGlobalSave = ['profile', 'notifications', 'business-hours', 'security', 'business-data'].includes(activeTab);

  if (loading) {
    return (
      <div className="settings-container">
        <div className="settings-loading">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="settings-container">
      <div className="settings-header">
        <div className="header-content">
          <h1>Settings</h1>
          <p>Manage your business configuration and preferences</p>
        </div>
        {showGlobalSave && (
          <button className="btn-save-settings" onClick={handleGlobalSave} disabled={saving}>
            {saving ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                Save Changes
              </>
            )}
          </button>
        )}
      </div>

      <div className="settings-content">
        <div className="settings-sidebar">
          <nav className="settings-nav">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <span className="nav-label">{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="settings-main">

          {/* â”€â”€ Profile â”€â”€ */}
          {activeTab === 'profile' && (
            <div className="settings-section">
              <h2>Business Profile</h2>
              <div className="settings-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>First Name</label>
                    <input
                      type="text"
                      value={profile.firstName}
                      onChange={e => setProfile(p => ({ ...p, firstName: e.target.value }))}
                      placeholder="First name"
                    />
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input
                      type="text"
                      value={profile.lastName}
                      onChange={e => setProfile(p => ({ ...p, lastName: e.target.value }))}
                      placeholder="Last name"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Email</label>
                    <input type="email" value={profile.email} readOnly disabled />
                    <span className="help-text">Email cannot be changed here</span>
                  </div>
                  <div className="form-group">
                    <label>Phone</label>
                    <input type="tel" value={profile.phone} readOnly disabled />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Timezone</label>
                    <select value={profile.timezone} onChange={e => setProfile(p => ({ ...p, timezone: e.target.value }))}>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">Eastern Time (ET)</option>
                      <option value="America/Chicago">Central Time (CT)</option>
                      <option value="America/Denver">Mountain Time (MT)</option>
                      <option value="America/Los_Angeles">Pacific Time (PT)</option>
                      <option value="Europe/London">GMT (London)</option>
                      <option value="Europe/Paris">CET (Paris)</option>
                      <option value="Asia/Dubai">GST (Dubai)</option>
                      <option value="Asia/Kolkata">IST (India)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Language</label>
                    <select value={profile.language} onChange={e => setProfile(p => ({ ...p, language: e.target.value }))}>
                      <option value="en">English</option>
                      <option value="es">Spanish</option>
                      <option value="fr">French</option>
                      <option value="de">German</option>
                      <option value="ar">Arabic</option>
                      <option value="hi">Hindi</option>
                      <option value="pt">Portuguese</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Business Description</label>
                  <textarea
                    value={profile.businessDescription}
                    onChange={e => setProfile(p => ({ ...p, businessDescription: e.target.value }))}
                    placeholder="Describe your business..."
                    rows="4"
                  />
                  <span className="help-text">Visible to customers on your WhatsApp profile</span>
                </div>
              </div>
            </div>
          )}

          {/* â”€â”€ WhatsApp API â”€â”€ */}
          {activeTab === 'whatsapp' && (
            <div className="settings-section">
              <h2>WhatsApp Business API Configuration</h2>
              <div className="settings-form">
                {loadingWaba ? (
                  <p>Loading WABA status...</p>
                ) : wabaStatus ? (
                  <>
                    <div className="api-status" style={{ marginBottom: '20px' }}>
                      <div className="status-indicator">
                        <span className={`status-dot ${wabaStatus.connectionStatus === 'connected' ? 'online' : 'offline'}`}></span>
                        <span>Connection: {wabaStatus.connectionStatus || 'Unknown'}</span>
                      </div>
                    </div>

                    <div className="form-group">
                      <label>WABA ID</label>
                      <input type="text" value={wabaStatus.wabaId || ''} readOnly disabled />
                    </div>

                    <div className="form-group">
                      <label>Phone Number ID</label>
                      <input type="text" value={wabaStatus.phoneNumberId || ''} readOnly disabled />
                    </div>

                    {wabaStatus.webhookUrl && (
                      <div className="form-group">
                        <label>Webhook URL</label>
                        <input type="text" value={wabaStatus.webhookUrl} readOnly disabled />
                        <span className="help-text">Configure via WABA setup to change</span>
                      </div>
                    )}

                    <div className="form-group">
                      <label>Business Verification</label>
                      <input type="text" value={wabaStatus.businessVerificationStatus || 'NOT_VERIFIED'} readOnly disabled />
                    </div>

                    <div className="form-group">
                      <label>Setup Progress</label>
                      {wabaStatus.setupProgress && (
                        <div className="setup-progress-list">
                          {Object.entries(wabaStatus.setupProgress).map(([key, done]) => (
                            <div key={key} className="setup-step">
                              <span className={`step-icon ${done ? 'done' : 'pending'}`}>{done ? 'âœ…' : 'â³'}</span>
                              <span>{key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="empty-state">
                    <div className="empty-icon">ðŸ’¬</div>
                    <p>No WhatsApp Business Account connected</p>
                    <span className="help-text">Complete the onboarding setup to connect your WABA</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* â”€â”€ Notifications â”€â”€ */}
          {activeTab === 'utility-service' && (
            <div className="settings-section">
              <h2>Utility Service Templates</h2>
              <UtilityServiceTemplateSettings />
            </div>
          )}

          {/* â”€â”€ Notifications â”€â”€ */}
          {activeTab === 'notifications' && (
            <div className="settings-section">
              <h2>Notification Preferences</h2>
              <div className="settings-form">
                <div className="toggle-group">
                  {[
                    { key: 'emailNotifications', label: 'Email Notifications',  desc: 'Receive notifications via email' },
                    { key: 'smsNotifications',   label: 'SMS Notifications',    desc: 'Receive notifications via SMS' },
                    { key: 'pushNotifications',  label: 'Push Notifications',   desc: 'Receive browser push notifications' },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="toggle-item">
                      <div className="toggle-info">
                        <label>{label}</label>
                        <span className="toggle-description">{desc}</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={notifications[key]}
                          onChange={() => setNotifications(n => ({ ...n, [key]: !n[key] }))}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  ))}
                </div>

                <div className="section-divider"><h3>Alert Types</h3></div>

                <div className="toggle-group">
                  {[
                    { key: 'newMessageAlerts', label: 'New Message Alerts',  desc: 'Get notified when you receive new messages' },
                    { key: 'broadcastAlerts',  label: 'Broadcast Alerts',    desc: 'Get notified about broadcast status' },
                    { key: 'templateAlerts',   label: 'Template Alerts',     desc: 'Get notified about template approval status' },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="toggle-item">
                      <div className="toggle-info">
                        <label>{label}</label>
                        <span className="toggle-description">{desc}</span>
                      </div>
                      <label className="toggle-switch">
                        <input
                          type="checkbox"
                          checked={notifications[key]}
                          onChange={() => setNotifications(n => ({ ...n, [key]: !n[key] }))}
                        />
                        <span className="slider"></span>
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* â”€â”€ Business Hours â”€â”€ */}
          {activeTab === 'business-hours' && (
            <div className="settings-section">
              <h2>Business Hours & Auto-Reply</h2>
              <div className="settings-form">
                <div className="form-group">
                  <label>Timezone *</label>
                  <select value={timezone} onChange={e => setTimezone(e.target.value)}>
                    <option value="UTC">UTC</option>
                    <option value="America/New_York">Eastern Time (ET)</option>
                    <option value="America/Chicago">Central Time (CT)</option>
                    <option value="America/Denver">Mountain Time (MT)</option>
                    <option value="America/Los_Angeles">Pacific Time (PT)</option>
                    <option value="Europe/London">GMT (London)</option>
                    <option value="Europe/Paris">CET (Paris)</option>
                    <option value="Asia/Dubai">GST (Dubai)</option>
                    <option value="Asia/Kolkata">IST (India)</option>
                  </select>
                </div>

                <div className="section-divider"><h3>Operating Hours</h3></div>

                <div className="business-hours-list">
                  {daysOfWeek.map(day => (
                    <div key={day.key} className="business-hour-item">
                      <div className="day-toggle">
                        <label className="toggle-switch">
                          <input
                            type="checkbox"
                            checked={businessHours[day.key]?.enabled || false}
                            onChange={e => setBusinessHours(h => ({ ...h, [day.key]: { ...h[day.key], enabled: e.target.checked } }))}
                          />
                          <span className="slider"></span>
                        </label>
                        <span className="day-label">{day.label}</span>
                      </div>
                      {businessHours[day.key]?.enabled ? (
                        <div className="time-inputs">
                          <input
                            type="time"
                            value={businessHours[day.key]?.start || '09:00'}
                            onChange={e => setBusinessHours(h => ({ ...h, [day.key]: { ...h[day.key], start: e.target.value } }))}
                          />
                          <span>to</span>
                          <input
                            type="time"
                            value={businessHours[day.key]?.end || '17:00'}
                            onChange={e => setBusinessHours(h => ({ ...h, [day.key]: { ...h[day.key], end: e.target.value } }))}
                          />
                        </div>
                      ) : (
                        <span className="closed-label">Closed</span>
                      )}
                    </div>
                  ))}
                </div>

                <div className="section-divider"><h3>Auto-Reply Settings</h3></div>

                <div className="toggle-item">
                  <div className="toggle-info">
                    <label>Enable Auto-Reply</label>
                    <span className="toggle-description">Automatically reply to messages outside business hours</span>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" checked={autoReply} onChange={() => setAutoReply(v => !v)} />
                    <span className="slider"></span>
                  </label>
                </div>

                {autoReply && (
                  <div className="form-group">
                    <label>Auto-Reply Message</label>
                    <textarea
                      value={autoReplyMessage}
                      onChange={e => setAutoReplyMessage(e.target.value)}
                      placeholder="Enter auto-reply message..."
                      rows="4"
                    />
                    <span className="help-text">Sent to customers who message outside business hours</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* â”€â”€ Security â”€â”€ */}
          {activeTab === 'security' && (
            <div className="settings-section">
              <h2>Security & Privacy</h2>
              <div className="settings-form">
                <div className="toggle-item">
                  <div className="toggle-info">
                    <label>Two-Factor Authentication</label>
                    <span className="toggle-description">Add an extra layer of security to your account</span>
                  </div>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={security.twoFactorAuthEnabled}
                      onChange={() => setSecurity(s => ({ ...s, twoFactorAuthEnabled: !s.twoFactorAuthEnabled }))}
                    />
                    <span className="slider"></span>
                  </label>
                </div>

                <div className="form-group">
                  <label>Session Timeout (minutes)</label>
                  <input
                    type="number"
                    value={security.sessionTimeout}
                    onChange={e => setSecurity(s => ({ ...s, sessionTimeout: e.target.value }))}
                    min="5"
                    max="120"
                  />
                  <span className="help-text">Automatically log out after inactivity (5“120 min)</span>
                </div>

                <div className="section-divider">
                  <h3>IP Whitelist</h3>
                  <span className="section-subtitle">Restrict access to specific IP addresses</span>
                </div>

                <div className="ip-whitelist">
                  <div className="ip-list">
                    {security.ipWhitelist.length === 0 ? (
                      <div className="empty-ip-list">
                        <span>No IP addresses whitelisted</span>
                        <span className="help-text">All IP addresses are allowed</span>
                      </div>
                    ) : (
                      security.ipWhitelist.map((ip, idx) => (
                        <div key={idx} className="ip-item">
                          <span>{ip}</span>
                          <button type="button" className="btn-remove" onClick={() => handleRemoveIp(ip)}>Remove</button>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="ip-add">
                    <input
                      type="text"
                      value={newIp}
                      onChange={e => setNewIp(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleAddIp()}
                      placeholder="Enter IP address (e.g., 192.168.1.1)"
                    />
                    <button type="button" className="btn-add-ip" onClick={handleAddIp}>Add IP</button>
                  </div>
                </div>

                <div className="section-divider"><h3>Change Password</h3></div>

                <div className="form-group">
                  <label>Current Password</label>
                  <input
                    type="password"
                    value={passwordForm.currentPassword}
                    onChange={e => setPasswordForm(f => ({ ...f, currentPassword: e.target.value }))}
                    placeholder="Enter current password"
                    autoComplete="current-password"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={e => setPasswordForm(f => ({ ...f, newPassword: e.target.value }))}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                    />
                  </div>
                  <div className="form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={e => setPasswordForm(f => ({ ...f, confirmPassword: e.target.value }))}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="btn-change-password"
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </div>
          )}

         
          {activeTab === 'qr-code' && (
            <div className="settings-section">
              <h2>QR Code Management</h2>
              <div className="settings-form">
                <div className="form-group">
                  <label>QR Code Format</label>
                  <select value={qrCodeFormat} onChange={e => setQrCodeFormat(e.target.value)}>
                    <option value="SVG">SVG (Vector)</option>
                    <option value="PNG">PNG (Raster)</option>
                  </select>
                  <span className="help-text">Choose the format for your QR codes</span>
                </div>

                <button type="button" className="btn-generate-qr" onClick={handleGenerateQRCode} disabled={loadingQR}>
                  {loadingQR ? (
                    <><span className="spinner"></span>Syncing...</>
                  ) : (
                    <>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect>
                        <rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect>
                      </svg>
                      Sync QR Codes
                    </>
                  )}
                </button>

                <div className="qr-create-actions">
                  <button
                    type="button"
                    className="btn-create-qr"
                    onClick={() => setShowQrCreateForm(prev => !prev)}
                  >
                    {showQrCreateForm ? 'Cancel' : 'Create New QR Code'}
                  </button>
                </div>

                {showQrCreateForm && (
                  <form className="qr-create-form" onSubmit={handleCreateQRCode}>
                    <div className="form-group">
                      <label>Prefilled Message</label>
                      <textarea
                        value={qrCreateForm.prefilledMessage}
                        onChange={e => setQrCreateForm(prev => ({ ...prev, prefilledMessage: e.target.value }))}
                        rows="3"
                        maxLength={1024}
                        placeholder="Enter message users should see when they scan the QR code"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Generate QR Image</label>
                      <select
                        value={qrCreateForm.generateQrImage}
                        onChange={e => setQrCreateForm(prev => ({ ...prev, generateQrImage: e.target.value }))}
                      >
                        <option value="SVG">SVG (Vector)</option>
                        <option value="PNG">PNG (Raster)</option>
                      </select>
                    </div>
                    <button type="submit" className="btn-create-qr-submit" disabled={qrSaving}>
                      {qrSaving ? 'Creating...' : 'Create QR Code'}
                    </button>
                  </form>
                )}

                <div className="section-divider"><h3>Your QR Codes ({qrCodes.length})</h3></div>

                {qrCodes.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon">ðŸ“±</div>
                    <p>No QR codes found</p>
                    <span className="help-text">Click "Sync QR Codes" to load your WhatsApp QR codes</span>
                  </div>
                ) : (
                  <div className="qr-codes-grid">
                    {qrCodes.map(qrCode => (
                      <div key={qrCode.code} className="qr-code-card">
                        <div className="qr-code-header">
                          <h4>{qrCode.code}</h4>
                          <span className={`status-badge ${qrCode.syncStatus}`}>{qrCode.syncStatus}</span>
                        </div>
                        {qrCode.qrImageUrl && (
                          <div className="qr-image-container">
                            <img src={qrCode.qrImageUrl} alt={`QR Code ${qrCode.code}`} />
                          </div>
                        )}
                        <div className="qr-code-info">
                          {(qrCode.prefilledMessage || qrCode.prefilled_message) && <p><strong>Message:</strong> {(qrCode.prefilledMessage || qrCode.prefilled_message).substring(0, 50)}...</p>}
                          <p><strong>Format:</strong> {qrCode.imageFormat}</p>
                          <p><strong>Synced:</strong> {new Date(qrCode.syncedAt).toLocaleDateString()}</p>
                        </div>
                        <div className="qr-code-actions">
                          <button type="button" className="btn-action-sm" onClick={() => handleDownloadQRCode(qrCode)} title="Download">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line>
                            </svg>
                          </button>
                          <button type="button" className="btn-action-sm" onClick={() => handleCopyQRCode(qrCode)} title="Copy link">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                              <rect x="8" y="2" width="8" height="4"></rect>
                            </svg>
                          </button>
                          <button type="button" className="btn-action-sm btn-danger" onClick={() => handleDeleteQRCode(qrCode.code)} title="Delete">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* -- Business Data -- */}
          {activeTab === 'business-data' && (
            <div className="settings-section">
              <h2>Invoice Business Details</h2>
              {loadingBusinessData ? (
                <p>Loading business data...</p>
              ) : (
                <div className="settings-form">

                  <p className="help-text" style={{ marginBottom: '14px' }}>
                    These details are used as seller information on invoice PDF.
                  </p>

                  {/* Brand Identity */}
                  <div className="section-divider"><h3>Brand Identity</h3></div>

                  <div className="form-group">
                    <label>Brand Logo (Invoice Header)</label>

                    {/* Uppy dropzone with ImageEditor */}
                    <div className="logo-uppy-wrap">
                      <div ref={logoDashboardRef} />
                    </div>

                    <button
                      type="button"
                      onClick={handleLogoUpload}
                      disabled={uploadingLogo || !hasLogoFile}
                      className="btn-save-settings"
                      style={{ marginTop: '4px' }}
                    >
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </button>
                    <span className="help-text" style={{ display: 'block', marginTop: '6px' }}>Select an image, edit/crop it, then click Upload Logo.</span>
                  </div>

                  <div className="form-group">
                    <label>Legal Business Name</label>
                    <input
                      type="text"
                      value={businessData.storeName}
                      onChange={e => setBusinessData(d => ({ ...d, storeName: e.target.value }))}
                      placeholder="Enter legal business name as shown on invoice"
                    />
                  </div>

                  {/* Billing Address */}
                  <div className="section-divider"><h3>Billing Address (Seller)</h3></div>

                  <div className="form-group">
                    <label>Address Line</label>
                    <textarea
                      value={businessData.storeAddress}
                      onChange={e => setBusinessData(d => ({ ...d, storeAddress: e.target.value }))}
                      placeholder="Enter registered business address"
                      rows="2"
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>City / District</label>
                      <input
                        type="text"
                        value={businessData.storeCity}
                        onChange={e => setBusinessData(d => ({ ...d, storeCity: e.target.value }))}
                        placeholder="City"
                      />
                    </div>
                    <div className="form-group">
                      <label>State / Province</label>
                      <input
                        type="text"
                        value={businessData.storeState}
                        onChange={e => setBusinessData(d => ({ ...d, storeState: e.target.value }))}
                        placeholder="State / Province"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>PIN / ZIP / Postal Code</label>
                      <input
                        type="text"
                        value={businessData.storeZip}
                        onChange={e => setBusinessData(d => ({ ...d, storeZip: e.target.value }))}
                        placeholder="Postal code"
                      />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        value={businessData.storeCountry}
                        onChange={e => setBusinessData(d => ({ ...d, storeCountry: e.target.value }))}
                        placeholder="e.g. India"
                      />
                    </div>
                  </div>

                  {/* Invoice Contact */}
                  <div className="section-divider"><h3>Invoice Contact Details</h3></div>

                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      value={businessData.storePhone}
                      onChange={e => setBusinessData(d => ({ ...d, storePhone: e.target.value }))}
                      placeholder="e.g. +91 98765 43210"
                    />
                  </div>

                  <div className="form-group">
                    <label>Billing Email Address</label>
                    <input
                      type="email"
                      value={businessData.storeEmail}
                      onChange={e => setBusinessData(d => ({ ...d, storeEmail: e.target.value }))}
                      placeholder="e.g. billing@yourcompany.com"
                    />
                  </div>

                  <div className="form-group">
                    <label>Website URL <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
                    <input
                      type="url"
                      value={businessData.storeWebsite}
                      onChange={e => setBusinessData(d => ({ ...d, storeWebsite: e.target.value }))}
                      placeholder="e.g. https://www.yourcompany.com"
                    />
                  </div>

                  {/* Tax & Bank */}
                  <div className="section-divider"><h3>Tax &amp; Bank Details</h3></div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>GSTIN / VAT / Tax Registration No.</label>
                      <input
                        type="text"
                        value={businessData.gstNumber}
                        onChange={e => setBusinessData(d => ({ ...d, gstNumber: e.target.value.toUpperCase() }))}
                        placeholder="e.g. 22AAAAA0000A1Z5"
                        maxLength="20"
                      />
                    </div>
                    <div className="form-group">
                      <label>PAN / Tax ID (optional)</label>
                      <input
                        type="text"
                        value={businessData.panNumber}
                        onChange={e => setBusinessData(d => ({ ...d, panNumber: e.target.value.toUpperCase() }))}
                        placeholder="e.g. AABCT1234F"
                        maxLength="10"
                      />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                    <div className="form-group">
                      <label>Bank Account Number</label>
                      <input
                        type="text"
                        value={businessData.bankAccountNumber}
                        onChange={e => setBusinessData(d => ({ ...d, bankAccountNumber: e.target.value }))}
                        placeholder="e.g. 123456789012345"
                      />
                    </div>
                    <div className="form-group">
                      <label>IFSC / SWIFT / Routing Code</label>
                      <input
                        type="text"
                        value={businessData.ifscCode}
                        onChange={e => setBusinessData(d => ({ ...d, ifscCode: e.target.value.toUpperCase() }))}
                        placeholder="e.g. SBIN0001234"
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '8px' }}>
                    <button
                      type="button"
                      className="btn-save-settings"
                      onClick={handleSaveBusinessData}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save Invoice Business Details'}
                    </button>
                  </div>

                </div>
              )}
            </div>
          )}
          {activeTab === 'team' && (
            <div className="settings-section">
              <h2>Team Management</h2>
              <div className="settings-form">
                <div className="empty-state">
                  <div className="empty-icon">ðŸ‘¥</div>
                  <p>Team management coming soon</p>
                  <span className="help-text">Multi-user access will be available in a future update</span>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Settings;